import { createApi } from '@reduxjs/toolkit/query/react';
import { IContentContext } from '../../common/interfaces/ContentContext.interface';
import { HttpStatus } from '../../common/enums/HttpStatus';
import { createBaseQuery } from './utils/createBaseQuery';
import { IPaginatedResponse } from '../../common/interfaces/PaginatedResponse.interface';

export const contentContextApi = createApi({
  reducerPath: 'contentContextApi',
  baseQuery: createBaseQuery('/content-contexts'),
  tagTypes: ['ContentContext'],
  endpoints: (builder) => ({
    fetchContentContext: builder.query<
      { code: HttpStatus; data: IPaginatedResponse<IContentContext> },
      { page: number; size: number }
    >({
      query: ({ page, size }) => `?page=${page}&size=${size}`,
      providesTags: (result) =>
        result
          ? [
              { type: 'ContentContext' as const, id: 'LIST' },
              ...result.data.data.map(({ id }) => ({
                type: 'ContentContext' as const,
                id,
              })),
            ]
          : [{ type: 'ContentContext' as const, id: 'LIST' }],
    }),

    createContentContext: builder.mutation<IContentContext, IContentContext>({
      query: (newContent) => ({
        url: '',
        method: 'POST',
        body: newContent,
      }),
      invalidatesTags: [{ type: 'ContentContext', id: 'LIST' }],
    }),

    updateContentContext: builder.mutation<
      IContentContext,
      { id: string; updatedContent: Partial<IContentContext> }
    >({
      query: ({ id, updatedContent }) => ({
        url: `/${id}`,
        method: 'PATCH',
        body: updatedContent,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'ContentContext', id },
        { type: 'ContentContext', id: 'LIST' },
      ],
    }),

    deleteContentContext: builder.mutation<
      { success: boolean; id: string },
      string
    >({
      query: (id) => ({
        url: `/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, id) => [
        { type: 'ContentContext', id },
        { type: 'ContentContext', id: 'LIST' },
      ],
    }),
  }),
});

export const {
  useFetchContentContextQuery,
  useCreateContentContextMutation,
  useUpdateContentContextMutation,
  useDeleteContentContextMutation,
} = contentContextApi;
